import React, { useEffect, useState } from "react";
import { api, fileUrl, apiErr } from "@/lib/api";
import { useT } from "@/i18n";
import { AppLayout } from "@/components/AppLayout";
import { BomView } from "@/components/BomView";
import { RenderCard } from "@/components/RenderCard";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, Sparkles, ImageOff, Calendar, Trash2 } from "lucide-react";

const STATUS = {
  draft: { label: "Ciornă", cls: "bg-slate-700 text-slate-200" },
  rendered: { label: "Randat", cls: "bg-blue-500/20 text-blue-300" },
  offer_requested: { label: "Ofertă cerută", cls: "bg-primary/20 text-primary" },
};

export default function Projects() {
  const navigate = useNavigate();
  const { t, lang } = useT();
  const [projects, setProjects] = useState(null);
  const [detail, setDetail] = useState(null);
  const [loadingDetail, setLoadingDetail] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/projects");
      setProjects(data.projects);
    } catch (e) { toast.error(apiErr(e)); }
  };
  useEffect(() => { load(); }, []);

  const open = async (id) => {
    setLoadingDetail(true);
    setDetail({});
    try {
      const { data } = await api.get(`/projects/${id}`);
      setDetail(data);
    } catch (e) { toast.error(apiErr(e)); setDetail(null); }
    finally { setLoadingDetail(false); }
  };

  const del = async (id, e) => {
    e.stopPropagation();
    if (!window.confirm(t("Ștergi acest proiect?"))) return;
    try { await api.delete(`/projects/${id}`); toast.success(t("Proiect șters")); load(); }
    catch (er) { toast.error(apiErr(er)); }
  };

  return (
    <AppLayout>
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-3xl font-extrabold text-white">{t("Proiectele mele")}</h1>
        <Button onClick={() => navigate("/studio")} data-testid="projects-new-btn" className="bg-primary hover:bg-primary/90 text-white">
          <Sparkles className="w-4 h-4 mr-1.5" /> {t("Proiect nou")}
        </Button>
      </div>

      {projects === null ? (
        <div className="flex justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-primary" /></div>
      ) : projects.length === 0 ? (
        <div className="text-center py-20 text-slate-400">
          <ImageOff className="w-12 h-12 mx-auto mb-4 text-slate-600" />
          <p>{t("Nu ai încă niciun proiect.")}</p>
          <Button onClick={() => navigate("/studio")} className="mt-4 bg-primary hover:bg-primary/90 text-white">{t("Creează primul proiect")}</Button>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {projects.map((p) => {
            const st = STATUS[p.status] || STATUS.draft;
            return (
              <div key={p.id} onClick={() => open(p.id)} data-testid={`project-card-${p.id}`}
                className="rounded-2xl overflow-hidden border border-slate-800 bg-card hover:border-primary/40 transition-colors cursor-pointer group">
                <div className="aspect-[4/3] bg-slate-900 relative overflow-hidden">
                  <img src={fileUrl(p.photo_path)} alt={p.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                  <Badge className={`absolute top-2 right-2 border-0 ${st.cls}`}>{t(st.label)}</Badge>
                </div>
                <div className="p-4">
                  <div className="flex items-start justify-between gap-2">
                    <h3 className="font-display font-semibold text-white truncate">{p.name}</h3>
                    <button onClick={(e) => del(p.id, e)} data-testid={`project-delete-${p.id}`} className="text-slate-500 hover:text-primary shrink-0">
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                  <div className="flex items-center gap-3 text-xs text-slate-500 mt-2">
                    <span className="capitalize">{p.space_type} · {p.room}</span>
                  </div>
                  <div className="flex items-center gap-3 text-xs text-slate-500 mt-1">
                    <span className="flex items-center gap-1"><Sparkles className="w-3 h-3" /> {p.render_count} {t("randări")}</span>
                    <span className="flex items-center gap-1"><Calendar className="w-3 h-3" /> {new Date(p.created_at).toLocaleDateString(lang === "en" ? "en-GB" : "ro-RO")}</span>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      <Dialog open={!!detail} onOpenChange={(o) => !o && setDetail(null)}>
        <DialogContent className="bg-card border-slate-800 max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-white">{detail?.project?.name || t("Detalii proiect")}</DialogTitle>
          </DialogHeader>
          {loadingDetail ? (
            <div className="flex justify-center py-16"><Loader2 className="w-7 h-7 animate-spin text-primary" /></div>
          ) : detail?.renders?.length > 0 ? (
            <div className="space-y-6">
              {detail.renders.map((r) => (
                <div key={r.id} className="space-y-4">
                  <div className="grid sm:grid-cols-3 gap-4">
                    {r.images.map((img) => (
                      <RenderCard key={img.style_key} render={img} originalPath={detail.project.photo_path} />
                    ))}
                  </div>
                  <BomView bom={r.bom} />
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-10 text-slate-400">
              <p>{t("Acest proiect nu are randări încă.")}</p>
              <Button onClick={() => navigate("/studio")} className="mt-4 bg-primary hover:bg-primary/90 text-white">{t("Generează randări")}</Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </AppLayout>
  );
}
